import { Link } from "react-router-dom";
import { ArrowRight, Users, Gauge, Timer, BatteryCharging, Briefcase, Fuel, Cog, Armchair } from "lucide-react";
import { useLanguage } from "@/i18n";
import Reveal, { MaskedLines } from "@/components/Reveal";

const CARS = [
  {
    name: "Mercedes-AMG A 45 S 4MATIC+",
    img: "https://images.unsplash.com/photo-1689170649735-a8c259851e2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxNZXJjZWRlcyUyMEFNRyUyMEE0NSUyMFMlMjBoYXRjaGJhY2t8ZW58MHx8fHwxNzkzNjMyMzQ2fDA&ixlib=rb-4.1.0&q=85",
    badge: { fr: "421 ch — Essence", de: "421 PS — Benzin", en: "421 hp — Petrol" },
    highlight: { fr: "La compacte la plus puissante de sa catégorie", de: "Der stärkste Kompakte seiner Klasse", en: "The most powerful compact in its class" },
    specs: {
      power: "421 ch",
      accel: "3,9 s",
      drivetrain: { fr: "4MATIC+ (4x4)", de: "4MATIC+ (Allrad)", en: "4MATIC+ (AWD)" },
      energy: { fr: "Essence — 8,4 l/100 km", de: "Benzin — 8,4 l/100 km", en: "Petrol — 8.4 l/100 km" },
      trunk: "370 L",
      seats: "5",
      gearbox: { fr: "Auto. 8 rapports", de: "Automatik 8-Gang", en: "8-speed automatic" },
    },
  },
  {
    name: "Tesla Model Y Grande Autonomie",
    img: "https://images.unsplash.com/photo-1669625397388-32934837bd3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxUZXNsYSUyME1vZGVsJTIwWSUyMGVsZWN0cmljJTIwU1VWfGVufDB8fHx8MTc5MzYzMjM0Nnww&ixlib=rb-4.1.0&q=85",
    badge: { fr: "≈ 514 ch — 100% électrique", de: "≈ 514 PS — 100% elektrisch", en: "≈ 514 hp — 100% electric" },
    highlight: { fr: "Le SUV électrique le plus vendu au monde", de: "Das meistverkaufte Elektro-SUV der Welt", en: "The world's best-selling electric SUV" },
    specs: {
      power: "≈ 514 ch",
      accel: "4,8 s",
      drivetrain: { fr: "Transmission intégrale", de: "Allradantrieb", en: "All-wheel drive" },
      energy: { fr: "Électrique — ≈ 533 km (WLTP)", de: "Elektro — ≈ 533 km (WLTP)", en: "Electric — ≈ 533 km (WLTP)" },
      trunk: "854 L + frunk",
      seats: "5",
      gearbox: { fr: "Automatique", de: "Automatik", en: "Automatic" },
    },
  },
  {
    name: "Mercedes-AMG CLA 35 4MATIC",
    img: "https://images.unsplash.com/photo-1596564239694-efb6211dd9ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxNZXJjZWRlcyUyMEFNRyUyMENMQSUyMHNlZGFuJTIwY291cGV8ZW58MHx8fHwxNzkzNjMyMzQ2fDA&ixlib=rb-4.1.0&q=85",
    badge: { fr: "306 ch — Essence", de: "306 PS — Benzin", en: "306 hp — Petrol" },
    highlight: { fr: "Le coupé 4 portes au tempérament sportif", de: "Das 4-Türer-Coupé mit sportlichem Charakter", en: "The 4-door coupé with a sporty character" },
    specs: {
      power: "306 ch",
      accel: "4,9 s",
      drivetrain: { fr: "4MATIC (4x4)", de: "4MATIC (Allrad)", en: "4MATIC (AWD)" },
      energy: { fr: "Essence — 8,3 l/100 km", de: "Benzin — 8,3 l/100 km", en: "Petrol — 8.3 l/100 km" },
      trunk: "460 L",
      seats: "5",
      gearbox: { fr: "Auto. 8 rapports", de: "Automatik 8-Gang", en: "8-speed automatic" },
    },
  },
  {
    name: "BYD Seal U DM-i",
    img: "https://images.unsplash.com/photo-1793002813983-d65e5364353a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxCWUQlMjBTZWFsJTIwZWxlY3RyaWMlMjBTVVYlMjBjYXJ8ZW58MHx8fHwxNzkzNjMyMzQ2fDA&ixlib=rb-4.1.0&q=85",
    badge: { fr: "330 ch — Hybride rechargeable", de: "330 PS — Plug-in-Hybrid", en: "330 hp — Plug-in hybrid" },
    highlight: { fr: "Le SUV hybride à la très longue autonomie", de: "Der Hybrid-SUV mit sehr grosser Reichweite", en: "The hybrid SUV with very long range" },
    specs: {
      power: "330 ch",
      accel: "8,9 s",
      drivetrain: { fr: "Traction", de: "Frontantrieb", en: "Front-wheel drive" },
      energy: { fr: "Hybride — ≈ 80 km électrique + essence", de: "Hybrid — ≈ 80 km elektrisch + Benzin", en: "Hybrid — ≈ 80 km electric + petrol" },
      trunk: "552 L",
      seats: "5",
      gearbox: { fr: "Automatique", de: "Automatik", en: "Automatic" },
    },
  },
];

const slug = (n) => n.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const SpecRow = ({ icon: Icon, label, value, last }) => (
  <div className={`flex items-center justify-between gap-4 py-2.5 ${last ? "" : "border-b border-slate-100"}`}>
    <span className="flex items-center gap-2.5 text-sm text-slate-500">
      <Icon className="h-4 w-4 shrink-0 text-red-600" strokeWidth={1.8} /> {label}
    </span>
    <span className="text-right text-sm font-semibold text-slate-900">{value}</span>
  </div>
);

const RentalPage = () => {
  const { t, lang } = useLanguage();
  const r = t.rental;

  return (
    <main data-testid="section-pole-location" className="min-h-screen bg-[#F8FAFC]">
      <section className="px-4 pb-16 pt-32 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <MaskedLines baseDelay={0.1} lines={[t.transport.badge]} lineClassName="font-mono text-xs uppercase tracking-[0.3em] text-red-600" />
          <MaskedLines
            baseDelay={0.25}
            lines={[r.title]}
            className="mt-4"
            lineClassName="text-3xl font-bold italic tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          />
          <Reveal delay={0.6}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">{r.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="px-4 pb-10 sm:px-6">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 lg:grid-cols-2">
          {CARS.map((car, i) => (
            <Reveal key={car.name} delay={i * 0.08}>
              <div data-testid={`rental-car-card-${slug(car.name)}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-28px_rgba(15,23,42,0.35)]">
                <div className="relative overflow-hidden">
                  <img
                    src={car.img}
                    alt={car.name}
                    className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-slate-900/90 px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                    {car.badge[lang]}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-600">{car.highlight[lang]}</p>
                  <h3 className="mt-2 text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {car.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">{r.cars[i]}</p>

                  <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 px-5 py-2">
                    <SpecRow icon={Gauge} label={r.labels.power} value={car.specs.power} />
                    <SpecRow icon={Timer} label={r.labels.accel} value={car.specs.accel} />
                    <SpecRow icon={Cog} label={r.labels.drivetrain} value={car.specs.drivetrain[lang]} />
                    <SpecRow icon={Fuel} label={r.labels.energy} value={car.specs.energy[lang]} />
                    <SpecRow icon={BatteryCharging} label={r.labels.gearbox} value={car.specs.gearbox[lang]} />
                    <SpecRow icon={Briefcase} label={r.labels.trunk} value={car.specs.trunk} />
                    <SpecRow icon={Armchair} label={r.labels.seats} value={car.specs.seats} last />
                  </div>

                  <Link
                    to="/contact"
                    data-testid={`rental-book-btn-${slug(car.name)}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-red-600 sm:self-start"
                  >
                    {r.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-4 pb-24 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="rounded-3xl bg-slate-900 p-8 text-center sm:p-12">
              <p className="mx-auto max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{r.note}</p>
              <Link
                to="/contact"
                data-testid="rental-cta-btn"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-red-600 px-8 py-4 text-sm font-bold text-white transition-colors duration-200 hover:bg-red-500"
              >
                {t.transport.ctaBtn}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default RentalPage;
